import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import {
  ACCEPT_COMPANY_INVITATION,
  ACCEPT_COMPANY_INVITATION_BY_ID,
  CANCEL_COMPANY_INVITATION,
  CREATE_COMPANY_INVITATION,
  REJECT_COMPANY_INVITATION,
  REJECT_COMPANY_INVITATION_BY_ID,
  RESEND_COMPANY_INVITATION,
} from '../graphql/mutations';
import type {
  CreateCompanyInvitationInput,
  MutationOutcome,
} from '../types/companyPortal.types';

/**
 * Invitation writes as `{ run, loading }`-style runners (§5.2/§9.2). Policy failures
 * (`CompanySettings.DomainNotAllowed`, duplicate pending, terminal states) come back in the
 * payload's `errors` — surfaced by the caller, never thrown. Management mutations refetch the
 * invitation table and the dashboard's pending count.
 */
export function useInvitationMutations() {
  const refetchQueries = ['CompanyInvitations', 'CompanyDashboard'];
  const [createM, { loading: creating }] = useMutation(CREATE_COMPANY_INVITATION, { refetchQueries });
  const [resendM, { loading: resending }] = useMutation(RESEND_COMPANY_INVITATION, { refetchQueries });
  const [cancelM, { loading: cancelling }] = useMutation(CANCEL_COMPANY_INVITATION, { refetchQueries });
  // Accept/reject refetch memberships — a successful accept is what grants portal access.
  const [acceptM, { loading: accepting }] = useMutation(ACCEPT_COMPANY_INVITATION, {
    refetchQueries: ['MyCompanyMemberships'],
  });
  const [rejectM, { loading: rejecting }] = useMutation(REJECT_COMPANY_INVITATION);
  // By-id (Invitation Discovery): accept grants access AND resolves the pending list; reject just
  // resolves the list. Both refetch MyCompanyInvitations so the discovery surfaces update in place.
  const [acceptByIdM, { loading: acceptingById }] = useMutation(ACCEPT_COMPANY_INVITATION_BY_ID, {
    refetchQueries: ['MyCompanyMemberships', 'MyCompanyInvitations'],
  });
  const [rejectByIdM, { loading: rejectingById }] = useMutation(REJECT_COMPANY_INVITATION_BY_ID, {
    refetchQueries: ['MyCompanyInvitations'],
  });

  const createInvitation = useCallback(
    async (input: CreateCompanyInvitationInput): Promise<MutationOutcome> => {
      const res = await createM({ variables: { input } });
      const errors = res.data?.createCompanyInvitation.errors ?? [];
      return { ok: errors.length === 0, errors };
    },
    [createM],
  );

  const resendInvitation = useCallback(
    async (invitationId: string): Promise<MutationOutcome> => {
      const res = await resendM({ variables: { input: { invitationId } } });
      const errors = res.data?.resendCompanyInvitation.errors ?? [];
      return { ok: errors.length === 0, errors };
    },
    [resendM],
  );

  const cancelInvitation = useCallback(
    async (invitationId: string): Promise<MutationOutcome> => {
      const res = await cancelM({ variables: { input: { invitationId } } });
      const errors = res.data?.cancelCompanyInvitation.errors ?? [];
      return { ok: errors.length === 0, errors };
    },
    [cancelM],
  );

  const acceptInvitation = useCallback(
    async (token: string): Promise<MutationOutcome & { companyId: string | null }> => {
      const res = await acceptM({ variables: { input: { token } } });
      const payload = res.data?.acceptCompanyInvitation;
      const errors = payload?.errors ?? [];
      return {
        ok: errors.length === 0 && Boolean(payload?.companyUser),
        errors,
        companyId: payload?.companyUser?.companyId ?? null,
      };
    },
    [acceptM],
  );

  const rejectInvitation = useCallback(
    async (token: string): Promise<MutationOutcome> => {
      const res = await rejectM({ variables: { input: { token } } });
      const errors = res.data?.rejectCompanyInvitation.errors ?? [];
      return { ok: errors.length === 0, errors };
    },
    [rejectM],
  );

  const acceptInvitationById = useCallback(
    async (invitationId: string): Promise<MutationOutcome & { companyId: string | null }> => {
      const res = await acceptByIdM({ variables: { input: { invitationId } } });
      const payload = res.data?.acceptCompanyInvitationById;
      const errors = payload?.errors ?? [];
      return {
        ok: errors.length === 0 && Boolean(payload?.companyUser),
        errors,
        companyId: payload?.companyUser?.companyId ?? null,
      };
    },
    [acceptByIdM],
  );

  const rejectInvitationById = useCallback(
    async (invitationId: string): Promise<MutationOutcome> => {
      const res = await rejectByIdM({ variables: { input: { invitationId } } });
      const errors = res.data?.rejectCompanyInvitationById.errors ?? [];
      return { ok: errors.length === 0, errors };
    },
    [rejectByIdM],
  );

  return {
    createInvitation,
    resendInvitation,
    cancelInvitation,
    acceptInvitation,
    rejectInvitation,
    acceptInvitationById,
    rejectInvitationById,
    creating,
    resending,
    cancelling,
    accepting,
    rejecting,
    acceptingById,
    rejectingById,
  };
}
